import React, { useState } from "react";
import { useT } from "@transifex/react";
import { useHistory } from "react-router-dom";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useBookContext } from "../../../context/BookContext";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import NoOwnContentModal from "../../../basic/create/noOwnContentModal/NoOwnContentModal";
import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import CtasWrapper from "../../../wrapper/create/ctasWrapper/CtasWrapper";
import VocabTrainingCreationHeader from "../../../complex/create/vocabTrainingCreationHeader/VocabTrainingCreationHeader";
import { getCreationReturnPath } from "../../../helpers/navigationHelper";
import VocabTrainingTitleBar from "../../../complex/create/vocabTrainingTitleBar/VocabTrainingTitleBar";
import ExistingActivityContent from "./ExistingActivityContent";
import { useOptionalSourcesReady } from "../../../hooks/useOptionalSourcesReady";
import RecentlyUsedContent from "./RecentlyUsedContent";
import { SourceType } from "../../../enums/sources";

import {
    VocabTrainingWrapper,
    SourceWrapper,
    SourceOptionItem,
    SourceOptionIconWrapper,
    SourceOptionArrowWrapper,
    SourceOptionItemTitleWrapper,
    SourceOptionItemTitle,
    SourceOptionItemDesc,
    StyledArrowIcon,
    StyledP6Icon,
} from "./StyledComponents";

const SelectVocabularySourceVocabTraining: React.FC = () => {
    const {
        decideGoToOwnSubjectsAction,
        openWebAppInAddCardsMode,
        resetAppStatus,
        clearSchoolbookSelection,
        clearOwnContentSelection,
        setSelectedUserSubject,
        setShouldSubjectSelectionBeDisabled,
        loadUserOwnSubjectUUIDsInfoIntoState,
    } = useTestCreationContext();
    const { creatingForSchoolClassId, setCreatingForSchoolClassId } = useGeneralContext();
    const { loadBookFromRecentBooks, setLastSourceSelected } = useBookContext();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const history = useHistory();
    const t = useT();

    const [isNoOwnContentModalOpen, setIsNoOwnContentModalOpen] = useState(false);
    const [needsSubjectsReload, setNeedsSubjectsReload] = useState(false);

    const { hasActivities, areOptionalSourcesReady } = useOptionalSourcesReady();

    const t_title = t("Which vocabulary would you like to assign?", {
        _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining",
    });
    const t_schoolbookTitle = t("Content from Schoolbook", {
        _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining",
    });
    const t_schoolbookDesc = t(
        "You can select your schoolbook and the relevant vocabulary (sorted by Units and Page)",
        { _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining" }
    );
    const t_ownVocabularyTitle = t("Self created content", {
        _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining",
    });
    const t_ownVocabularyDesc = t("Use your own self created content", {
        _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining",
    });
    const t_back = t("Back", { _tags: "CreateVocabTraining,SelectVocabularySourceVocabTraining" });

    const handleSchoolbookClick = () => {
        // Symmetric to the own-content path: drop any own-content selection so it doesn't linger
        // into the schoolbook flow.
        clearOwnContentSelection();
        history.push("/create/select-book");
    };

    const handleSelectRecent = async (contentId: string, isOwnSubject: boolean) => {
        if (!isOwnSubject) {
            setLastSourceSelected(SourceType.BOOK);
            loadBookFromRecentBooks(contentId);
            return;
        }

        const subjectsInfo = await loadUserOwnSubjectUUIDsInfoIntoState([contentId]);
        if (!subjectsInfo?.[contentId]) {
            return;
        }

        setSelectedUserSubject(subjectsInfo[contentId]);
        setShouldSubjectSelectionBeDisabled(true);
        setLastSourceSelected(SourceType.OWN);
        history.push("/create/select-own-source");
    };

    const handleOwnContentClick = async () => {
        const action = await decideGoToOwnSubjectsAction(needsSubjectsReload);
        if (action === "CONTINUE") {
            clearSchoolbookSelection();
            history.push("/create/select-own-source");
        } else if (action === "POPUP") {
            setIsNoOwnContentModalOpen(true);
        }
    };

    const handleGoToWebAppClick = () => {
        openWebAppInAddCardsMode();
        setIsNoOwnContentModalOpen(false);
        setNeedsSubjectsReload(true);
    };

    function handleBackButton() {
        const returnPath = getCreationReturnPath(creatingForSchoolClassId);
        setCreatingForSchoolClassId("");
        resetAppStatus();
        history.push(returnPath);
    }

    const renderSourceOption = (
        testId: string,
        iconName: string,
        title: string,
        description: string,
        onClick: () => void
    ) => {
        return (
            <SourceOptionItem
                data-testid={testId}
                onClick={onClick}
            >
                <SourceOptionIconWrapper>
                    <StyledP6Icon name={iconName} />
                </SourceOptionIconWrapper>
                <SourceOptionItemTitleWrapper>
                    <SourceOptionItemTitle>{title}</SourceOptionItemTitle>
                    <SourceOptionItemDesc>{description}</SourceOptionItemDesc>
                </SourceOptionItemTitleWrapper>
                <SourceOptionArrowWrapper>
                    <StyledArrowIcon
                        onClick={() => {}}
                        direction={"RIGHT"}
                    />
                </SourceOptionArrowWrapper>
            </SourceOptionItem>
        );
    };

    return (
        <VocabTrainingCreationHeader hasFullHeightContent>
            <VocabTrainingWrapper>
                <CreationFlowSection>
                    <VocabTrainingTitleBar />
                </CreationFlowSection>
                <p className={`${getStyleBasedOnBreakpoint(Breakpoint.MD, "px-s py-s")} text-l text-p6-grey-dark`}>
                    {t_title}
                </p>
                <SourceWrapper>
                    {areOptionalSourcesReady && (
                        <>
                            <RecentlyUsedContent onSelectRecent={handleSelectRecent} />
                            <ExistingActivityContent hasActivities={hasActivities} />
                        </>
                    )}
                    {renderSourceOption(
                        "source-option-schoolbook",
                        "library",
                        t_schoolbookTitle,
                        t_schoolbookDesc,
                        handleSchoolbookClick
                    )}
                    {renderSourceOption(
                        "source-option-own-content",
                        "cards-2",
                        t_ownVocabularyTitle,
                        t_ownVocabularyDesc,
                        handleOwnContentClick
                    )}
                </SourceWrapper>
            </VocabTrainingWrapper>
            <CtasWrapper isSticky>
                <ResponsiveButton
                    onClick={handleBackButton}
                    iconName="chevron-left"
                    label={t_back}
                >
                    <StyledButton
                        onClick={handleBackButton}
                        arrowDirection={"LEFT"}
                        buttonStyle={"BLANK"}
                    >
                        {t_back}
                    </StyledButton>
                </ResponsiveButton>
            </CtasWrapper>
            <NoOwnContentModal
                isOpen={isNoOwnContentModalOpen}
                closeModalCallback={() => setIsNoOwnContentModalOpen(false)}
                continueCallback={handleGoToWebAppClick}
            />
        </VocabTrainingCreationHeader>
    );
};

export default SelectVocabularySourceVocabTraining;
